<template>
  <d-container fluid class="main-content-container px-4 product-form">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Produkty</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja produktu</template>
          <template v-else>Dodawanie produktu</template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/produkty')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs
          :nav-class="allDataLoaded ? validationResults : ''"
          card>
          <!-- Dane ogólne -->
          <d-tab title="Dane ogólne" active>
            <h4 class="mb-3 pb-3 border-bottom">
              Dane ogólne
            </h4>

            <validation-observer
              ref="product-form"
              tag="form">
              <div class="form-group">
                <label for="name">
                  Nazwa produktu:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="code">
                  Kod produktu:
                </label>

                <validation-provider
                  rules="required"
                  name="code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="price_code">
                  Kod ceny:
                </label>

                <validation-provider
                  rules="required"
                  name="price_code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.price_code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="price">
                  Cena:
                </label>

                <validation-provider
                  rules="required|min_value:0"
                  name="price"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="number"
                    v-model="data.price" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="material_id">
                  Materiał:
                </label>

                <v-select
                  :options="productMaterials"
                  v-model="data.material_id"
                  placeholder="Wybierz materiał"
                  :searchable="true"
                  :clearable="true"
                  :multiple="false"
                  :disabled="isMainProduct"
                  :reduce="option => option.value"
                  label="text"
                  @input="data.color_id = null">
                </v-select>

                <small v-if="isMainProduct">
                  Produkty, gdzie kod produktu i kod ceny jest identyczny mają dostępne zawsze wszystkie nici, materiały i kolory.
                </small>
              </div>

              <div
                v-if="data.material_id"
                class="form-group">
                <label for="color_id">
                  Kolor:
                </label>

                <v-select
                  :options="materialColors"
                  v-model="data.color_id"
                  placeholder="Wybierz kolor"
                  :searchable="true"
                  :clearable="true"
                  :multiple="false"
                  :disabled="isMainProduct"
                  :reduce="option => option.value"
                  label="text">
                </v-select>

                <small v-if="isMainProduct">
                  Produkty, gdzie kod produktu i kod ceny jest identyczny mają dostępne zawsze wszystkie nici, materiały i kolory.
                </small>
              </div>

              <div class="form-group">
                <label for="strand_id">
                  Nić:
                </label>

                <v-select
                  :options="productStrands"
                  v-model="data.strand_id"
                  placeholder="Wybierz nić"
                  :searchable="true"
                  :clearable="true"
                  :multiple="false"
                  :disabled="isMainProduct"
                  :reduce="option => option.value"
                  label="text">
                </v-select>

                <small v-if="isMainProduct">
                  Produkty, gdzie kod produktu i kod ceny jest identyczny mają dostępne zawsze wszystkie nici, materiały i kolory.
                </small>
              </div>

              <div class="form-group">
                <label for="intro">
                  Krótki opis:
                </label>

                <validation-provider
                  rules="required"
                  name="intro"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.intro" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                key="procedures-wrapper"
                class="form-group">
                <label for="procedures">
                  Dostępne procedury:
                </label>

                <v-select
                  key="product-form-procedures"
                  :options="productProcedures"
                  v-model="data.procedures"
                  placeholder="Wybierz procedury"
                  :searchable="true"
                  :clearable="true"
                  :multiple="true"
                  label="text">
                </v-select>
              </div>

              <div
                key="sizes-wrapper"
                class="form-group">
                <label for="sizes">
                  Dostępne rozmiary:
                </label>

                <v-select
                  key="product-form-sizes"
                  :options="productSizes"
                  v-model="data.sizes"
                  placeholder="Wybierz dostępne rozmiary"
                  :searchable="true"
                  :clearable="true"
                  :multiple="true"
                  :disabled="isMainProduct"
                  label="text">
                </v-select>

                <small v-if="isMainProduct">
                  Produkty, gdzie kod produktu i kod ceny jest identyczny mają dostępne zawsze wszystkie rozmiary.
                </small>
              </div>
            </validation-observer>

            <div
              v-if="!allDataLoaded"
              class="pb-3 pt-3 text-center">
              Trwa ładowanie danych&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'product-form',
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedMaterials && this.loadedStrands && this.loadedSizes && this.loadedProcedures;
    },
    isEdit () {
      return !!this.$route.params.id;
    },
    isMainProduct () {
      if (this.data.code !== '' && this.data.code === this.data.price_code) {
        return true;
      }

      return false;
    },
    productStrands () {
      return this.strands.map(strand => ({
        text: strand.name,
        value: strand.id
      }));
    },
    productMaterials () {
      return this.materials.map(color => ({
        text: color.name,
        value: color.id
      }));
    },
    materialColors () {
      if (this.data.material_id) {
        let foundedMaterial = this.materials.filter(material => material.id === this.data.material_id);

        if (foundedMaterial[0]) {
          return foundedMaterial[0].colors.map(color => ({
            text: color.name,
            value: color.id
          }));
        }

        return [];
      }

      return [];
    },
    productProcedures () {
      return this.procedures.map(procedure => ({
        text: procedure.name,
        value: procedure.id
      }));
    },
    productSizes () {
      return this.sizes.map(size => ({
        text: size.name,
        value: size.id
      }));
    },
    validationResults () {
      if (!this.allDataLoaded) {
        return false;
      }

      let errors = this.$refs['product-form'].errors;
      let errorTabs = [];
      let results = [
        ['error-1', ['name', 'code', 'price_code', 'price', 'intro']]
      ];

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errors[key] && errors[key].length) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      return errorTabs.join(' ');
    }
  },
  watch: {
    'allDataLoaded': function (newValue) {
      if (newValue && this.isEdit && this.data.color_id) {
        let foundedMaterial = this.materials.filter(material => material.colors.map(color => color.id).indexOf(this.data.color_id) > -1);

        if (foundedMaterial[0]) {
          Vue.set(this.data, 'material_id', foundedMaterial[0].id);
        }
      }
    },
    'isMainProduct': function (newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.data.material_id = null;
        this.data.color_id = null;
        this.data.strand_id = null;
        this.data.sizes = null;
      }
    }
  },
  data() {
    return {
      materials: [],
      sizes: [],
      strands: [],
      data: {
        id: 0,
        name: '',
        code: '',
        price_code: '',
        price: null,
        color_id: null,
        material_id: null,
        strand_id: null,
        intro: '',
        sizes: null,
        procedures: null
      },
      loaded: false,
      loadedMaterials: false,
      loadedProcedures: false,
      loadedSizes: false,
      loadedStrands: false,
      loadError: false,
      procedures: []
    };
  },
  mounted () {
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }

    this.loadAdditionalData();
  },
  methods: {
    loadItemData () {
      // material_id is not loaded as it is based on the color_id
      FormUtils.loadItemData(this, {
        endpointBase: '/products/item/',
        instanceFields: ['id', 'name', 'code', 'price_code', 'price', 'intro', 'sizes', 'color_id', 'strand_id', 'procedures'],
        loadedKey: 'loaded',
        successAction: () => {
          if (this.data.sizes) {
            this.data.sizes = this.data.sizes.map(size => ({
              text: size.name,
              value: size.id
            }));
          }

          if (this.data.procedures) {
            this.data.procedures = this.data.procedures.map(procedure => ({
              text: procedure.name,
              value: procedure.id
            }));
          }
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/materials/items',
        outputKey: 'materials',
        loadedKey: 'loadedMaterials',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/strands/items',
        outputKey: 'strands',
        loadedKey: 'loadedStrands',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/procedures/items',
        outputKey: 'procedures',
        loadedKey: 'loadedProcedures',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/sizes/items',
        outputKey: 'sizes',
        loadedKey: 'loadedSizes',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['product-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit ? '/products/update/' + this.id : '/products/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano produkt',
        successText: 'Wybrany produkt został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja produktu nie powiodła się. Spróbuj ponownie.',
        successAction: (response) => {
          if (!this.isEdit && response.item && response.item.id) {
            this.$router.push('/produkty/edycja/' + parseInt(response.item.id, 10));
          } else if (this.isEdit) {
            this.loaded = false;
            this.loadItemData();
          } else {
            this.$router.push('/produkty')
          }
        }
      });
    },
    prepareFormDataToSend () {
      // material_id is not sent, because it is based on color_id
      let formDataToSend = {
        name: this.data.name,
        code: this.data.code,
        price_code: this.data.price_code,
        price: this.data.price,
        intro: this.data.intro
      };

      if (this.data.sizes) {
        formDataToSend.sizes = this.data.sizes.map(size => size.value);
      }

      if (this.data.procedures) {
        formDataToSend.procedures = this.data.procedures.map(procedure => procedure.value);
      }

      if (this.data.color_id) {
        formDataToSend.color_id = parseInt(this.data.color_id, 10);
      } else if (this.data.color_id === null) {
        formDataToSend.color_id = false;
      }

      if (this.data.strand_id) {
        formDataToSend.strand_id = parseInt(this.data.strand_id, 10);
      } else if (this.data.strand_id === null) {
        formDataToSend.strand_id = false;
      }

      return formDataToSend;
    }
  }
};
</script>

<style lang="scss" scoped>
.product-form {
  /deep/ .v-select {
    .vs__dropdown-toggle {
      border: 1px solid #e1e5eb;

      .vs__selected-options {
        .vs__selected {
          margin-right: 3px!important;
        }
      }
    }
  }
}
</style>
